import { ISO_4217_MINOR_UNITS } from '../currency/iso4217.js';

/**
 * Minor unit exponent per currency: how many decimal places it uses, e.g.
 * EUR = 2 (cents), JPY = 0. Every active ISO 4217 currency (DEV-162), so a
 * currency an admin adds to Currencies offered (CAD first) is valued and
 * formatted like any other; a code that is not ISO 4217 has no entry.
 */
export const CURRENCY_EXPONENTS: Readonly<Record<string, number>> = ISO_4217_MINOR_UNITS;

export type Currency = 'EUR' | 'USD' | 'GBP' | 'CHF' | 'JPY' | 'SEK' | 'NOK' | 'DKK' | 'PLN';

export const CURRENCIES: readonly Currency[] = [
  'EUR', 'USD', 'GBP', 'CHF', 'JPY', 'SEK', 'NOK', 'DKK', 'PLN',
] as const;

/**
 * Immutable Money value type. Amount is always in the smallest currency unit
 * (e.g., cents for EUR, whole yen for JPY).
 */
export interface Money {
  readonly amount: bigint;
  readonly currency: Currency;
}

export type MoneyErrorCode =
  | 'CURRENCY_MISMATCH'
  | 'DIVISION_BY_ZERO'
  | 'INVALID_AMOUNT'
  | 'INVALID_CURRENCY';

export interface MoneyError {
  readonly code: MoneyErrorCode;
  readonly message: string;
}

export type Result<T, E> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: E };

export function ok<T>(value: T): Result<T, never> {
  return { ok: true, value };
}

export function err<E>(error: E): Result<never, E> {
  return { ok: false, error };
}

export function moneyError(code: MoneyErrorCode, message: string): MoneyError {
  return { code, message };
}

/**
 * Create a Money value from minor units (e.g., cents).
 */
export function fromMinorUnits(amount: bigint, currency: Currency): Money {
  return { amount, currency };
}

/**
 * Extract the minor units (bigint) from a Money value.
 */
export function toMinorUnits(money: Money): bigint {
  return money.amount;
}

/**
 * Get the minor unit exponent for a currency.
 */
export function getMinorUnitExponent(currency: Currency): number {
  const exponent = CURRENCY_EXPONENTS[currency];
  // All Currency enum values have entries in CURRENCY_EXPONENTS
  if (exponent === undefined) {
    return 2;
  }
  return exponent;
}
